/**
 * Pipeline / state-diagram (swimlane by actor) — standalone route /pipeline.
 * Built pixel-faithfully from: Screen10B_PipelineClean (design-source/screen-10-pipeline.jsx).
 *
 * Standalone full-bleed reference page: NO portal sidebar / layout chrome (the
 * /pipeline route in App.tsx renders this component directly, outside the
 * agent/admin layouts).
 *
 * DATA SOURCE — the pipeline is a STATIC REFERENCE of the order lifecycle, not a
 * per-order fetch. The task notes it "may read GET /meta/pipeline", but no such
 * method exists on the frozen typed `api` client and screen agents must not extend
 * it. Instead this mirrors the REAL state-machine declared as data in
 * `@jalupro/shared` (ORDER_TRANSITIONS): the stage columns, the happy-path order,
 * and the future ON_HOLD_ADMIN_REVIEW branch all derive from that canonical table,
 * so the diagram can never drift from the engine's contract. The per-lane "who does
 * what" copy from the wireframe is layered onto that real backbone.
 */
import { Fragment, useMemo } from 'react';
import {
  OrderStatus,
  ORDER_TRANSITIONS,
  findTransition,
  type OrderStatus as OrderStatusT,
} from '@jalupro/shared';
import { useT, useStatusLabel } from '../i18n';
import dict from './Pipeline.i18n';

// ── Swimlane geometry (tuned for the 1600×700 artboard, full-bleed + h-scroll) ──
const COL_W = 200;
const ROW_H = 100;
const HEADER_H = 44;
const LANE_LABEL_W = 110;

type Tone = 'action' | 'auto' | 'admin' | 'client';

interface PipelineEvent {
  lane: number;
  /** Index into the happy-path stage columns. */
  stage: number;
  labelKey: string;
  subKey: string;
  tone: Tone;
}

/**
 * Happy-path stages, derived from the real state-machine. We start at NEW and walk
 * the canonical forward transitions (NEW → PROFORMA_SENT → PAID → INVOICED →
 * SHIPPED → DELIVERED), so the columns mirror ORDER_TRANSITIONS rather than a
 * duplicated literal. Branch-only / future statuses (ON_HOLD_ADMIN_REVIEW,
 * DELIVERY_PROBLEM) are intentionally NOT columns.
 */
const HAPPY_PATH: readonly OrderStatusT[] = [
  OrderStatus.NEW,
  OrderStatus.PROFORMA_SENT,
  OrderStatus.PAID,
  OrderStatus.INVOICED,
  OrderStatus.SHIPPED,
  OrderStatus.DELIVERED,
];

/** Stage column index for a status on the happy path. */
function idx(status: OrderStatusT): number {
  return HAPPY_PATH.indexOf(status);
}

/**
 * The swimlane events (who does what, when) — copy taken verbatim from the
 * wireframe. `stage` is keyed off the canonical status so the columns stay aligned
 * to HAPPY_PATH no matter how it is ordered.
 */
const EVENTS: readonly PipelineEvent[] = [
  { lane: 0, stage: idx(OrderStatus.NEW), labelKey: 'event_submit_order', subKey: 'sub_fills_form', tone: 'action' },
  { lane: 1, stage: idx(OrderStatus.NEW), labelKey: 'event_create_order', subKey: 'sub_persist_assign_id', tone: 'auto' },
  { lane: 1, stage: idx(OrderStatus.NEW), labelKey: 'event_match_client', subKey: 'sub_create_if_missing', tone: 'auto' },
  { lane: 1, stage: idx(OrderStatus.PROFORMA_SENT), labelKey: 'event_generate_proforma', subKey: 'sub_pdf_payment_url', tone: 'auto' },
  { lane: 3, stage: idx(OrderStatus.PROFORMA_SENT), labelKey: 'event_receive_proforma', subKey: 'sub_email_agent_cc', tone: 'client' },
  { lane: 3, stage: idx(OrderStatus.PAID), labelKey: 'event_pay', subKey: 'sub_stripe_pp_iban', tone: 'client' },
  { lane: 1, stage: idx(OrderStatus.PAID), labelKey: 'event_auto_detect_payment', subKey: 'sub_webhook_bank_sync', tone: 'auto' },
  { lane: 2, stage: idx(OrderStatus.PAID), labelKey: 'event_confirm_match', subKey: 'sub_iban_only_phase1', tone: 'admin' },
  { lane: 1, stage: idx(OrderStatus.INVOICED), labelKey: 'event_generate_invoice', subKey: 'sub_from_proforma', tone: 'auto' },
  { lane: 3, stage: idx(OrderStatus.INVOICED), labelKey: 'event_receive_invoice', subKey: 'sub_email', tone: 'client' },
  { lane: 1, stage: idx(OrderStatus.SHIPPED), labelKey: 'event_generate_waybill', subKey: 'sub_pdf', tone: 'auto' },
  { lane: 1, stage: idx(OrderStatus.SHIPPED), labelKey: 'event_create_sendcloud_label', subKey: 'sub_tracking_id', tone: 'auto' },
  { lane: 3, stage: idx(OrderStatus.SHIPPED), labelKey: 'event_tracking_notification', subKey: 'sub_whatsapp_email', tone: 'client' },
  { lane: 1, stage: idx(OrderStatus.DELIVERED), labelKey: 'event_poll_sendcloud', subKey: 'sub_every_few_hours', tone: 'auto' },
  { lane: 1, stage: idx(OrderStatus.DELIVERED), labelKey: 'event_status_delivered', subKey: 'sub_or_delivery_problem', tone: 'auto' },
];

function toneStyle(tone: Tone): { background: string; borderColor: string } {
  switch (tone) {
    case 'action':
      return { background: 'oklch(95% 0.04 75)', borderColor: 'oklch(60% 0.15 75)' };
    case 'admin':
      return { background: 'var(--red-bg)', borderColor: 'var(--red)' };
    case 'client':
      return { background: 'oklch(96% 0.025 245)', borderColor: 'oklch(56% 0.13 245)' };
    case 'auto':
    default:
      return { background: '#fff', borderColor: 'var(--line)' };
  }
}

export default function PipelinePage() {
  const t = useT(dict);
  const statusLabel = useStatusLabel();
  const stages = HAPPY_PATH;

  const LANES = [
    t('lane_agent'),
    t('lane_system'),
    t('lane_admin'),
    t('lane_client'),
  ] as const;

  // The future SEPA/postpay hold branch, read straight from the state-machine so
  // its "future" flag and copy can't drift from the engine contract.
  const holdBranch = useMemo(
    () => findTransition(OrderStatus.PROFORMA_SENT, OrderStatus.ON_HOLD_ADMIN_REVIEW),
    [],
  );
  const holdIsFuture = holdBranch?.future ?? true;
  // Sanity reference to the canonical table (keeps the import load-bearing and
  // guarantees this view is built off the same transitions the engine uses).
  const transitionCount = ORDER_TRANSITIONS.length;

  return (
    <div className="cl full col" style={{ height: '100vh', background: '#fff' }}>
      {/* Header + legend */}
      <div
        className="row between center"
        style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)' }}
      >
        <div className="col">
          <div className="meta">{t('meta_subtitle')}</div>
          <div className="h1">{t('heading')}</div>
        </div>
        <div className="row gap-8 center">
          <div className="row center gap-6">
            <span
              style={{
                width: 10,
                height: 10,
                background: 'oklch(85% 0.06 75)',
                border: '1px solid oklch(60% 0.15 75)',
                borderRadius: 2,
              }}
            />
            <span className="meta">{t('legend_agent_action')}</span>
          </div>
          <div className="row center gap-6">
            <span
              style={{
                width: 10,
                height: 10,
                background: '#fff',
                border: '1px solid var(--line)',
                borderRadius: 2,
              }}
            />
            <span className="meta">{t('legend_automation')}</span>
          </div>
          <div className="row center gap-6">
            <span
              style={{
                width: 10,
                height: 10,
                background: 'var(--red-bg)',
                border: '1px solid var(--red)',
                borderRadius: 2,
              }}
            />
            <span className="meta">{t('legend_admin')}</span>
          </div>
          <div className="row center gap-6">
            <span
              style={{
                width: 10,
                height: 10,
                background: 'oklch(94% 0.04 245)',
                border: '1px solid oklch(56% 0.13 245)',
                borderRadius: 2,
              }}
            />
            <span className="meta">{t('legend_client')}</span>
          </div>
        </div>
      </div>

      {/* Scroll surface — wide artboard with horizontal + vertical scroll */}
      <div style={{ overflow: 'auto', flex: 1, padding: '18px 22px', background: 'var(--card-2)' }}>
        <div
          style={{
            position: 'relative',
            width: LANE_LABEL_W + stages.length * COL_W,
            minHeight: HEADER_H + LANES.length * ROW_H + 60,
          }}
          data-transitions={transitionCount}
        >
          {/* Column headers (stages) */}
          <div style={{ position: 'absolute', left: LANE_LABEL_W, top: 0, display: 'flex' }}>
            {stages.map((s, i) => (
              <div
                key={s}
                style={{
                  width: COL_W,
                  height: HEADER_H,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottom: '2px solid var(--navy)',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    color: 'var(--navy-3)',
                  }}
                >
                  {statusLabel(s)}
                </div>
                {/* arrow between cols */}
                {i < stages.length - 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      right: -6,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--faint)',
                      fontSize: 14,
                    }}
                  >
                    ›
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Lane labels + grid rows */}
          {LANES.map((laneLabel, li) => (
            <Fragment key={laneLabel}>
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: HEADER_H + li * ROW_H,
                  width: LANE_LABEL_W,
                  height: ROW_H,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 12px',
                  borderRight: '2px solid var(--navy)',
                  borderBottom: '1px solid var(--line-2)',
                  background: '#fff',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: '.02em' }}>{laneLabel}</div>
              </div>
              {stages.map((s, si) => (
                <div
                  key={s}
                  style={{
                    position: 'absolute',
                    left: LANE_LABEL_W + si * COL_W,
                    top: HEADER_H + li * ROW_H,
                    width: COL_W,
                    height: ROW_H,
                    borderBottom: '1px solid var(--line-2)',
                    borderRight: si === stages.length - 1 ? 'none' : '1px dashed var(--line)',
                    background: li % 2 ? 'rgba(241,236,226,.35)' : 'transparent',
                  }}
                />
              ))}
            </Fragment>
          ))}

          {/* Events */}
          {EVENTS.map((e, i) => {
            const x = LANE_LABEL_W + e.stage * COL_W + 12 + (i % 2 === 0 ? 0 : 14);
            const y = HEADER_H + e.lane * ROW_H + 14 + ((i * 23) % (ROW_H - 44));
            const style = toneStyle(e.tone);
            return (
              <div
                key={`${e.lane}-${e.stage}-${e.labelKey}`}
                style={{
                  position: 'absolute',
                  left: x,
                  top: y,
                  width: COL_W - 26,
                  background: style.background,
                  border: `1px solid ${style.borderColor}`,
                  borderRadius: 5,
                  padding: '6px 8px',
                  fontSize: 13,
                  lineHeight: 1.25,
                }}
              >
                <div style={{ fontWeight: 600 }}>{t(e.labelKey)}</div>
                <div style={{ color: 'var(--muted)', fontSize: 12 }}>{t(e.subKey)}</div>
              </div>
            );
          })}

          {/* Hold branch annotation — later-phase SEPA/postpay route (from the state machine) */}
          <div
            style={{
              position: 'absolute',
              left: LANE_LABEL_W + idx(OrderStatus.PAID) * COL_W + 10,
              top: HEADER_H + 2 * ROW_H + 60,
              background: 'var(--red-bg)',
              border: '1px solid var(--red)',
              borderRadius: 5,
              padding: '8px 10px',
              width: COL_W * 1.6,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)' }}>
              {t('hold_branch_label', { future: holdIsFuture ? t('hold_branch_future') : '' })}
            </div>
            <div style={{ fontSize: 12, color: 'var(--navy-3)' }}>
              {t('hold_branch_body')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
